import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { usePageHeight } from "hooks/hooks";
import OverLayLoading, { Loading } from "ui/Loading";
import GridCustomPage from "ui/grid/GridPage";
import {
	UserListGridColumns,
	UserListDynamicColumns,
} from "./utils/UserListGridColumns";
import {
	allUsersGroupRolesApi,
	getUserListApi,
	UserListFilter,
} from "api/user";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import Filter from "./utils/UserListFilter";
import { TextButton } from "ui/Button";
import "./style.css";

import styles from "styles.module.css";
import { UserListItem } from "../../../interface/user";
import { COLOR, ICON_NAMES } from "utils/constant";
import { CustomError, IconButtonActionType } from "interface";
import { PageTypePath } from "utils/pages";
import { useInfoStore } from "store/error";
import { deleteUserApi } from "api/administration";
import { useNavigate } from "react-router";
import UserListMobileList from "./UserListMobileList";

const UserListPage: FC<{}> = () => {
	const pageSize = 60;
	const { t } = useTranslation();
	const overlayRef = useRef<HTMLDivElement>(null);
	const checkItemsRef = useRef<HTMLDivElement>(null);
	const gridRef = useRef<HTMLDivElement>(null);
	const headerRef = useRef<HTMLImageElement | null>(null);
	const pdfRef = useRef<GridPDFExport>(null);
	const excelRef = useRef<ExcelExport>(null);
	const [isMobileView, setIsMobileView] = useState<boolean>(() => {
		if (typeof window === "undefined") {
			return false;
		}

		return window.innerWidth < 768;
	});
	const [data, setData] = useState<UserListItem[]>([]);
	const [mobileLoading, setMobileLoading] = useState<boolean>(false);
	const [sortData, setSortData] = useState<{
		asc: "ASC" | "DESC";
		sortOrder: number | null;
	}>();
	const [dynamicColumns, setDynamicColumns] = useState<UserListDynamicColumns>({
		buyers: { field: "buyers", title: t("Buyer"), hidden: true },
		vessels: { field: "vessels", title: t("Vessel"), hidden: true },
		terminals: { field: "terminals", title: t("Terminal"), hidden: true },
	});

	const [filterOptions, setFilterOptions] = useState<UserListFilter>({
		take: pageSize,
		skip: 0,
	});
	const { setError, setInfo, resetStore } = useInfoStore();
	const [forceUpdate, setForceUpdate] = useState<boolean>(false);
	const mobileRequestInProgress = useRef(false);
	const mobileDataFinished = useRef(false);
	const mobileNextSkip = useRef(0);

	const [userGroupRoles, setUserGroupRoles] = useState<any[]>([]);
	// let { path, id, action } = useParams();

	const navigate = useNavigate();

	const requestMobileData = (skip: number, take: number, resetData = false) => {
		if (mobileRequestInProgress.current || mobileDataFinished.current) {
			return;
		}

		mobileRequestInProgress.current = true;
		setMobileLoading(true);

		getUserListApi({
			...filterOptions,
			skip,
			take,
		})
			.then((response) => {
				const incoming = (response?.data || []) as UserListItem[];

				if (incoming.length === 0) {
					mobileDataFinished.current = true;
					if (resetData) {
						setData([]);
					}
					return;
				}

				mobileNextSkip.current = skip + take;

				if (resetData) {
					setData(incoming);
				} else {
					setData((prev) => [...prev, ...incoming]);
				}

				const loadedCount = skip + incoming.length;
				if (loadedCount >= (response?.totalCount || 0)) {
					mobileDataFinished.current = true;
				}
			})
			.catch((error) => {
				if (error.response?.data) {
					if (error.response.data?.errors) {
						setError(new CustomError(error.response?.data.title));
					} else {
						setError(new CustomError(error.response?.data.returnText));
					}
				} else {
					setError(new CustomError(error.message));
				}
			})
			.finally(() => {
				mobileRequestInProgress.current = false;
				setMobileLoading(false);
			});
	};

	const exportGridAsPDF = () => {
		if (pdfRef.current !== null) {
			pdfRef.current.save();
		}
	};

	const exportGridAsExcel = () => {
		if (excelRef.current !== null) {
			excelRef.current.save();
		}
	};

	const handleColumns = () => {
		return UserListGridColumns({
			ref: headerRef,
			roles: userGroupRoles,
			deleteUser: requestDeleteUser,
			dynamicColumns,
			setDynamicColumns,
		});
	};

	useEffect(() => {
		const updateMobileView = () => setIsMobileView(window.innerWidth < 768);
		window.addEventListener("resize", updateMobileView);

		return () => {
			window.removeEventListener("resize", updateMobileView);
		};
	}, []);

	useEffect(() => {
		allUsersGroupRolesApi()
			.then((response) => {
				setUserGroupRoles(response.data);
			})
			.catch((error) => {
				const err = new CustomError(t("ErrorTitle"));
				const showCustomError = true;
				setError(err, showCustomError);
				setData([]);
			})
			.finally(() => {});
	}, []);

	useEffect(() => {
		if (!isMobileView) {
			return;
		}

		mobileDataFinished.current = false;
		mobileNextSkip.current = 0;
		setData([]);

		requestMobileData(0, pageSize, true);
	}, [isMobileView, filterOptions, forceUpdate]);

	const requestDeleteUser = (data: UserListItem) => {
		setInfo({
			title: t("Delete") + " " + t("User"),
			description: t("DeleteUserConfirmation"),
			show: true,
			actions: [
				{
					text: t("Cancel"),
					btn: () => {
						//console.log("Delete cancelled");
						resetStore();
					},
					textColor: COLOR.StatusRed,
				},
				{
					text: t("Delete"),
					btn: () => {
						if (!data.id) {
							const err = new CustomError(
								t("ErrorDescription"),
								t("ErrorTitle"),
							);
							setError(err);
							resetStore();
						} else {
							handleDeleteUser(data.id);
						}
					},
					textColor: COLOR.StatusGreen,
				},
			],
		});
	};
	const handleDeleteUser = (id: string) => {
		deleteUserApi({ userId: id as string })
			.then(() => {
				setForceUpdate((prev) => !prev);
				setInfo({
					title: t("Success"),
					description: t("DeleteSuccess"),
					show: true,
					actions: [
						{
							text: t("Ok"),
							btn: () => {
								resetStore();
							},
							textColor: COLOR.StatusGreen,
						},
					],
				});
			})
			.catch((error) => {
				const err = new CustomError(t("UserDeleteFailed", "UserDeleteFailed"));
				const showCustomError = true;
				setError(err, showCustomError);
			});
	};

	const handleSortOrderValue = (name: string) => {
		const items = [
			{
				name: "firstname",
				value: 1,
			},
			{ name: "lastname", value: 2 },
			{ name: "email", value: 3 },
			{ name: "vessels", value: 4 },
			{ name: "buyers", value: 5 },
		];
		const found = items.find((item) => item.name === name);
		if (!found) return null;
		return found.value;
	};
	if (!isMobileView && userGroupRoles.length === 0) {
		return (
			<div className={styles.PageContainer}>
				<Loading show={true} />
			</div>
		);
	}
	return (
		<div className={styles.PageContainer}>
			<div ref={overlayRef} style={{ display: "none" }}>
				<OverLayLoading />
			</div>
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>{t("UserList")}</legend>

				<legend>
					<TextButton
						text={t("NewUserAgreement")}
						onClick={() => navigate(`/${PageTypePath.NewUser}`)}
						iconName={ICON_NAMES.Plus}
						// disabled={!pageAccess || !pageAccess.create}
					/>
				</legend>
			</div>

			<div ref={checkItemsRef}>
				<Filter
					setFilterOptions={setFilterOptions}
					filterOptions={filterOptions}
					exportAsPDF={exportGridAsPDF}
					exportAsExcel={exportGridAsExcel}
					pageSize={pageSize}
					roles={userGroupRoles}
					setDynamicColumns={setDynamicColumns}
				/>
			</div>
			<div style={{}} ref={gridRef}>
				{isMobileView ? (
					<UserListMobileList
						items={data}
						loading={mobileLoading}
						onEdit={(item) => {
							window.open(
								`/${PageTypePath.UserInformation}/${item.id}`,
								"_blank",
							);
						}}
						onDelete={requestDeleteUser}
						onLoadMore={() =>
							requestMobileData(mobileNextSkip.current, pageSize)
						}
					/>
				) : (
					<GridCustomPage
						dataKey="id"
						forceUpdate={forceUpdate}
						gridColumns={handleColumns()}
						exportGridAsPDFRef={pdfRef}
						exportGridAsExcelRef={excelRef}
						apiMethod={getUserListApi}
						itemAboveHeight={50}
						sortable={true}
						serverFilter={true}
						filter={filterOptions}
						showDetailIcon={false}
						lastColWidth="150px"
						serverSortValue={
							sortData
								? sortData
								: {
										asc: null,
										sortOrder: null,
									}
						}
						handleSortChange={(e) => {
							if (e.sort && e.sort.length > 0) {
								setSortData({
									asc: e.sort[0].dir === "asc" ? "ASC" : "DESC",
									sortOrder: handleSortOrderValue(e.sort[0].field) || null,
								});
								return;
							}
							setSortData(undefined);
						}}
						filterOperators={{
							text: [
								{ text: "grid.filterContainsOperator", operator: "contains" },
							],
						}}
						serverFilterMap={[
							{
								serverFieldName: "firstName",
								filterFieldName: "firstname",
							},
							{
								serverFieldName: "lastName",
								filterFieldName: "lastname",
							},
							{
								serverFieldName: "email",
								filterFieldName: "email",
							},
							{
								serverFieldName: "role",
								filterFieldName: "roles",
							},
						]}
						lastColBtns={[
							{
								alwaysShow: true,
								onclick: (data) => {
									window.open(
										`/${PageTypePath.UserInformation}/${data.dataItem.id}`,
										"_blank",
									);
								},
								actionType: IconButtonActionType.Update,
								iconName: ICON_NAMES.Edit,
							},
							{
								alwaysShow: true,
								onclick: (data) => {
									requestDeleteUser(data.dataItem);
								},
								actionType: IconButtonActionType.Update,
								iconName: ICON_NAMES.Delete,
							},
						]}
					/>
				)}
			</div>
		</div>
	);
};

export default UserListPage;
